import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import s from './BecomeACompany.css';
import lpApi from '../../helpers/lpApi';
import CompanyAuth from './CompanyAuth';

// ============================================================================
// "Become a Company" onboarding wizard — Phase B1 of the Large Projects module.
//
// All backend operations live on besthand-api and are reached through the
// `/lp-graphql` server proxy via the `lpApi` helper. We never duplicate LP
// resolvers in besthand-web (per the architecture decision).
//
// Wizard is NON-BLOCKING: the user can move freely between steps, save & resume
// (the created companyProfile id is persisted in localStorage and re-hydrated
// from `myCompanyProfile` on load), and is only gated from the FINAL submit
// until the minimum required data is present (≥1 license + ≥1 GL insurance,
// which is exactly what submitCompanyKyb enforces server-side).
// ============================================================================

// ---- GraphQL operations (shape mirrors besthand-api recover/lp-integration) ----

const Q_MY_COMPANY = `
  query {
    myCompanyProfile {
      status
      errorMessage
      companyProfile {
        id legalName dbaName einLast4 kybStatus kybStatusReason
        headquartersAddressLine1 headquartersAddressLine2 headquartersCity
        headquartersState headquartersZip serviceRadiusMiles operatingStates
        specialties websiteUrl logoUrl tagline yearFounded stripeConnectAccountId
        sectorSlug sectorName sectorIsConstruction
      }
    }
  }
`;

const Q_MY_LICENSES = `
  query {
    myLicenses {
      status
      errorMessage
      licenses { id type number state classification issuingBody issueDate expirationDate verificationStatus }
    }
  }
`;

const Q_MY_INSURANCES = `
  query {
    myInsurances {
      status
      errorMessage
      insurances { id type carrier policyNumber carrierPhone coverageAmount expirationDate verificationStatus }
    }
  }
`;

const Q_SECTORS = `
  query { lpSectors { status lpSectors { id name isConstruction groupLabel sortOrder } } }
`;

const M_CREATE_COMPANY = `
  mutation (
    $legalName: String!, $dbaName: String, $ein: String,
    $headquartersAddressLine1: String, $headquartersCity: String,
    $headquartersState: String, $headquartersZip: String, $sector: String
  ) {
    createCompanyProfile(
      legalName: $legalName, dbaName: $dbaName, ein: $ein,
      headquartersAddressLine1: $headquartersAddressLine1,
      headquartersCity: $headquartersCity,
      headquartersState: $headquartersState,
      headquartersZip: $headquartersZip, sector: $sector
    ) {
      status errorMessage
      companyProfile { id legalName dbaName einLast4 kybStatus }
    }
  }
`;

// KYB — Middesk onboarding autofill. ENV-GATED on the besthand-api side
// (MIDDESK_API_KEY). When Middesk is not configured this returns a clean
// "Middesk not configured." status 200 with null fields, so the autofill simply
// does nothing and the manual form is unchanged.
const Q_LOOKUP_EIN = `
  query ($ein: String, $name: String) {
    lookupBusinessByEin(ein: $ein, name: $name) {
      status errorMessage lookupLegalName lookupState lookupStatus
    }
  }
`;

const M_UPDATE_COMPANY = `
  mutation (
    $dbaName: String, $headquartersAddressLine1: String, $headquartersAddressLine2: String,
    $headquartersCity: String, $headquartersState: String, $headquartersZip: String,
    $serviceRadiusMiles: Int, $operatingStates: [String], $specialties: [String],
    $websiteUrl: String, $logoUrl: String, $tagline: String, $yearFounded: Int, $sector: String
  ) {
    updateCompanyProfile(
      dbaName: $dbaName, headquartersAddressLine1: $headquartersAddressLine1,
      headquartersAddressLine2: $headquartersAddressLine2, headquartersCity: $headquartersCity,
      headquartersState: $headquartersState, headquartersZip: $headquartersZip,
      serviceRadiusMiles: $serviceRadiusMiles, operatingStates: $operatingStates,
      specialties: $specialties, websiteUrl: $websiteUrl, logoUrl: $logoUrl,
      tagline: $tagline, yearFounded: $yearFounded, sector: $sector
    ) {
      status errorMessage
      companyProfile {
        id legalName dbaName kybStatus specialties operatingStates serviceRadiusMiles
        websiteUrl logoUrl tagline yearFounded
      }
    }
  }
`;

const M_ADD_LICENSE = `
  mutation (
    $companyId: String!, $type: String!, $number: String!, $state: String!,
    $classification: String, $issuingBody: String, $issueDate: String,
    $expirationDate: String, $documentUrl: String
  ) {
    addLicense(
      companyId: $companyId, type: $type, number: $number, state: $state,
      classification: $classification, issuingBody: $issuingBody, issueDate: $issueDate,
      expirationDate: $expirationDate, documentUrl: $documentUrl
    ) {
      status errorMessage
      license { id type number state verificationStatus }
    }
  }
`;

const M_ADD_INSURANCE = `
  mutation (
    $companyId: String!, $type: String!, $carrier: String!, $policyNumber: String!,
    $carrierPhone: String, $coverageAmount: Float, $expirationDate: String, $coiDocumentUrl: String
  ) {
    addInsurance(
      companyId: $companyId, type: $type, carrier: $carrier, policyNumber: $policyNumber,
      carrierPhone: $carrierPhone, coverageAmount: $coverageAmount,
      expirationDate: $expirationDate, coiDocumentUrl: $coiDocumentUrl
    ) {
      status errorMessage
      insurance { id type carrier policyNumber verificationStatus }
    }
  }
`;

// submitCompanyKyb: validates docs, then (HYBRID light flow) creates a Stripe
// Custom Connect account and attaches the bank account + company representative
// IN-APP from the args below — mirroring the worker addPayout.js LIGHT flow.
// It transitions DRAFT -> PENDING and returns:
//   stripeReady=true / currentlyDueCount=0 -> fully onboarded in-app, NO redirect.
//   onboardingUrl (non-null)               -> Stripe still needs a few items
//                                             (e.g. beneficial owners); shown as a
//                                             clearly-labelled secondary link, NOT
//                                             an automatic full-page redirect.
// refreshUrl/returnUrl are only used for that hosted FALLBACK link.
const M_SUBMIT_KYB = `
  mutation (
    $refreshUrl: String, $returnUrl: String,
    $accountNumber: String, $routingNumber: String,
    $transitNumber: String, $institutionNumber: String,
    $currency: String, $country: String,
    $repFirstName: String, $repLastName: String,
    $repDobDay: Int, $repDobMonth: Int, $repDobYear: Int,
    $repSsnLast4: String,
    $repAddressLine1: String, $repAddressCity: String,
    $repAddressState: String, $repAddressPostalCode: String,
    $repAddressCountry: String,
    $repEmail: String, $repPhone: String
  ) {
    submitCompanyKyb(
      refreshUrl: $refreshUrl, returnUrl: $returnUrl,
      accountNumber: $accountNumber, routingNumber: $routingNumber,
      transitNumber: $transitNumber, institutionNumber: $institutionNumber,
      currency: $currency, country: $country,
      repFirstName: $repFirstName, repLastName: $repLastName,
      repDobDay: $repDobDay, repDobMonth: $repDobMonth, repDobYear: $repDobYear,
      repSsnLast4: $repSsnLast4,
      repAddressLine1: $repAddressLine1, repAddressCity: $repAddressCity,
      repAddressState: $repAddressState, repAddressPostalCode: $repAddressPostalCode,
      repAddressCountry: $repAddressCountry,
      repEmail: $repEmail, repPhone: $repPhone
    ) {
      status errorMessage onboardingUrl stripeReady currentlyDueCount
      companyProfile { id kybStatus stripeConnectAccountId }
    }
  }
`;

// syncCompanyKybStatus: polled after returning from Stripe-hosted onboarding.
const M_SYNC_KYB = `
  mutation {
    syncCompanyKybStatus {
      status errorMessage stripeReady
      companyProfile { id kybStatus stripeConnectAccountId stripeConnectTosAcceptedAt }
    }
  }
`;

// ---- Reference data (matches besthand-api ALLOWED_SPECIALTIES + enum sets) ----

const SPECIALTIES = [
  ['KITCHEN', 'Kitchen'],
  ['BATHROOM', 'Bathroom'],
  ['BASEMENT', 'Basement'],
  ['EXTERIOR', 'Exterior'],
  ['ROOFING', 'Roofing'],
  ['INSTALLATION', 'Installation'],
  ['GENERAL', 'General Contracting'],
];

const LICENSE_TYPES = ['HIC', 'GENERAL', 'ELECTRICAL', 'PLUMBING', 'HVAC', 'ROOFING', 'OTHER'];
const INSURANCE_TYPES = [
  ['GENERAL_LIABILITY', 'General Liability (required)'],
  ['WORKERS_COMP', "Workers' Comp"],
  ['BOND', 'Bond'],
  ['PROFESSIONAL_LIABILITY', 'Professional Liability'],
  ['OTHER', 'Other'],
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA',
  'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT',
  'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
];

// Canadian provinces/territories — used by the in-app payout form when the
// representative's home country is CA (the api forwards state -> Stripe).
const CA_PROVINCES = [
  'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT',
];

// Payout countries the besthand-api HYBRID flow accepts (US/CA only).
const PAYOUT_COUNTRIES = [
  ['US', 'United States'],
  ['CA', 'Canada'],
];

// Team-size brackets — Thumbtack-style "how big is your team" selector. Persisted
// into the company tagline-adjacent UI; see note in renderBusiness (team size has
// no dedicated besthand-api column yet, so it stays a guided local capture that
// feeds the review summary — never silently dropped server-side).
const TEAM_SIZES = [
  ['SOLO', 'Just me'],
  ['2_5', '2–5'],
  ['6_10', '6–10'],
  ['11_25', '11–25'],
  ['26_PLUS', '26+'],
];

// Service-area model — mirrors Thumbtack's "where do you work" step.
const SERVICE_AREA_MODES = [
  ['TRAVEL', 'I travel to customers', 'Set your home base ZIP and how far you’ll drive.'],
  ['ONSITE', 'Customers come to me', 'Clients visit your shop or office.'],
  ['REMOTE', 'Remote / hybrid', 'Some or all work is done remotely.'],
];

const STEPS = [
  { key: 'business', label: 'Profile & services' },
  { key: 'documents', label: 'Verification' },
  { key: 'connect', label: 'Payouts' },
  { key: 'review', label: 'Review & submit' },
];

// kybStatus pipeline shown by the status tracker. besthand-api uses DRAFT ->
// PENDING (after submit) -> APPROVED / REJECTED / SUSPENDED. We render the
// human-friendly draft / submitted / under_review / approved sequence and map
// PENDING onto "submitted / under review".
const STATUS_FLOW = [
  { key: 'DRAFT', label: 'Draft' },
  { key: 'PENDING', label: 'Submitted' },
  { key: 'REVIEW', label: 'Under review' },
  { key: 'APPROVED', label: 'Approved' },
];

const LS_KEY = 'lp_company_id';

// ----------------------------------------------------------------------------

const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d={d} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function emptyBusiness() {
  return {
    legalName: '',
    sector: '',
    dbaName: '',
    ein: '',
    headquartersAddressLine1: '',
    headquartersCity: '',
    headquartersState: '',
    headquartersZip: '',
    serviceRadiusMiles: '',
    serviceAreaMode: 'TRAVEL',
    websiteUrl: '',
    logoUrl: '',
    tagline: '',
    yearFounded: '',
    teamSize: '',
    specialties: [],
    operatingStates: [],
  };
}

// In-app HYBRID payout form (mirrors besthand-api submitCompanyKyb args +
// the worker addPayout.js LIGHT flow). Bank country drives which bank fields
// show: US -> routing number; CA -> transit + institution number. The raw
// values are forwarded to Stripe by besthand-api and never persisted locally.
function emptyPayout() {
  return {
    country: 'US', // US | CA — bank + default rep country
    currency: 'usd',
    accountNumber: '',
    routingNumber: '', // US
    transitNumber: '', // CA
    institutionNumber: '', // CA
    repFirstName: '',
    repLastName: '',
    repDobDay: '',
    repDobMonth: '',
    repDobYear: '',
    repSsnLast4: '', // US SSN last 4 / CA SIN last 4 (Stripe id_number)
    repAddressLine1: '',
    repAddressCity: '',
    repAddressState: '',
    repAddressPostalCode: '',
    repAddressCountry: 'US',
    repEmail: '',
    repPhone: '',
  };
}

// Client-side guard before we even hit the network. The server re-validates type
// (multer fileFilter) and size, but a friendly local check avoids a round-trip.
const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;
const ALLOWED_DOC_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
const ALLOWED_LOGO_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

class BecomeACompany extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      sectors: [], // LP sector catalog for the picker (search + free-text)
      // `authed` gates the whole wizard: when false we render the DETACHED
      // company landing + signup + sign-in (CompanyAuth) instead of the "please
      // log in" error. It flips true once the /lp-graphql proxy answers without
      // a 401 (i.e. the id_token cookie is present and valid).
      authed: false,
      // true only after the user signs up/in through CompanyAuth (the company path).
      // A logged-in client/admin WITHOUT a company profile must NOT auto-enter the
      // wizard — they stay on the detached CompanyAuth landing to sign in as a company.
      companyAuthed: false,
      step: 0,
      saving: false,
      error: '',
      notice: '',
      company: null, // the persisted companyProfile (id, kybStatus, ...)
      business: emptyBusiness(),
      licenses: [],
      insurances: [],
      licenseDraft: this.emptyLicense(),
      insuranceDraft: this.emptyInsurance(),
      // Per-target upload progress/error keyed by a slot name ('license',
      // 'insurance', 'ownerId', 'logo'). { uploading, error, fileName }.
      uploads: {},
      // Owner-ID document URL (uploaded here, but the binary is verified again by
      // Stripe during Connect onboarding; we keep our own copy for admin review).
      ownerIdUrl: '',
      // In-app HYBRID payout form (bank + company representative).
      payout: emptyPayout(),
      // Hosted-onboarding FALLBACK link returned by submitCompanyKyb when Stripe
      // still has currently_due requirements after in-app collection. Rendered as
      // a clearly-labelled secondary action — NOT an automatic redirect.
      payoutFallbackUrl: '',
      // KYB — Middesk autofill: { busy, notice } for the EIN-lookup prefill.
      // Inert when MIDDESK_API_KEY is unset (resolver returns "not configured").
      middeskLookup: { busy: false, notice: '' },
    };
  }

  // KYB — best-effort Middesk autofill. Called when the EIN field loses focus
  // (once name + a plausible EIN are present). Prefills legal name + operating
  // state from Middesk's match. Fully optional + fail-safe: any error, or
  // Middesk being disabled, is a silent no-op — the manual form is untouched.
  async middeskAutofill() {
    const b = this.state.business || {};
    const rawEin = (b.ein || '').replace(/\D/g, '');
    // Need an EIN-shaped value (9 digits) to be worth a lookup.
    if (rawEin.length !== 9) return;
    if (this.state.middeskLookup.busy) return;
    this.setState({ middeskLookup: { busy: true, notice: '' } });
    try {
      const res = await lpApi(Q_LOOKUP_EIN, { ein: rawEin, name: b.legalName || null });
      const env = res && res.data && res.data.lookupBusinessByEin;
      if (!env || env.status !== 200 || env.errorMessage || !env.lookupLegalName) {
        // Disabled / no match / error -> silent no-op.
        this.setState({ middeskLookup: { busy: false, notice: '' } });
        return;
      }
      this.setState((st) => {
        const biz = { ...st.business };
        let filled = false;
        // Only autofill EMPTY fields — never overwrite what the user typed.
        if (!(biz.legalName || '').trim() && env.lookupLegalName) {
          biz.legalName = env.lookupLegalName; filled = true;
        }
        if (env.lookupState && !(biz.headquartersState || '').trim()) {
          biz.headquartersState = env.lookupState; filled = true;
        }
        if (
          env.lookupState &&
          Array.isArray(biz.operatingStates) &&
          !biz.operatingStates.includes(env.lookupState)
        ) {
          biz.operatingStates = [...biz.operatingStates, env.lookupState]; filled = true;
        }
        return {
          business: biz,
          middeskLookup: {
            busy: false,
            notice: filled ? `Prefilled from Middesk: ${env.lookupLegalName}.` : '',
          },
        };
      });
    } catch (e) {
      // Never break onboarding on a lookup failure.
      this.setState({ middeskLookup: { busy: false, notice: '' } });
    }
  }

  // eslint-disable-next-line class-methods-use-this
  emptyLicense() {
    return {
      type: 'HIC',
      number: '',
      state: '',
      classification: '',
      issuingBody: '',
      issueDate: '',
      expirationDate: '',
      // Set from the upload route's returned absolute documentUrl (see uploadDoc).
      documentUrl: '',
    };
  }

  // eslint-disable-next-line class-methods-use-this
  emptyInsurance() {
    return {
      type: 'GENERAL_LIABILITY',
      carrier: '',
      policyNumber: '',
      carrierPhone: '',
      coverageAmount: '',
      expirationDate: '',
      // Set from the upload route's returned absolute documentUrl (see uploadDoc).
      coiDocumentUrl: '',
    };
  }

  componentDidMount() {
    this.bootstrap();
    // If the user just returned from Stripe-hosted onboarding (?connect=return),
    // poll the sync mutation so the status tracker reflects reality.
    if (typeof window !== 'undefined' && /[?&]connect=return/.test(window.location.search)) {
      this.setState({ step: 2 });
    }
  }

  async bootstrap() {
    // Load the LP sector catalog for the picker (best-effort; free-text still works).
    lpApi(Q_SECTORS).then((r) => {
      const list = r && r.data && r.data.lpSectors && r.data.lpSectors.lpSectors;
      if (Array.isArray(list)) this.setState({ sectors: list.filter((x) => x && x.name) });
    }).catch(() => {});
    try {
      const res = await lpApi(Q_MY_COMPANY);
      if (res.errors) {
        // Not logged in: the proxy got a 401 from besthand-api (no/invalid
        // id_token cookie). Render the detached company landing + auth instead
        // of an error. Any other error is surfaced but still treated as
        // logged-out so the user always has a clear way in.
        const msg = this.firstError(res);
        const unauth = /401|unauth|log\s?in|token/i.test(msg);
        this.setState({ loading: false, authed: false, error: unauth ? '' : msg });
        return;
      }
      const cp = res.data && res.data.myCompanyProfile && res.data.myCompanyProfile.companyProfile;
      if (cp) {
        if (typeof window !== 'undefined') {
          try { window.localStorage.setItem(LS_KEY, String(cp.id)); } catch (e) { /* noop */ }
        }
        this.hydrateFromCompany(cp);
        await this.loadDocs();
        // Coming back from Stripe — refresh status.
        if (typeof window !== 'undefined' && /[?&]connect=return/.test(window.location.search)) {
          await this.syncKyb();
        }
      }
      // Show the wizard only if this user is actually a company (has a profile) OR
      // came in through CompanyAuth. A logged-in client/admin with no company profile
      // falls back to the detached CompanyAuth landing (sign in / create a company).
      this.setState({ loading: false, authed: !!cp || this.state.companyAuthed });
    } catch (e) {
      this.setState({ loading: false, error: `Could not load your company: ${e.message}` });
    }
  }

  // Called by CompanyAuth after it sets the id_token cookie (signup or sign-in).
  // Re-run bootstrap so the wizard hydrates with the now-authenticated session.
  onAuthenticated() {
    this.setState({ loading: true, authed: true, companyAuthed: true, error: '', notice: '' }, () => {
      this.bootstrap();
    });
  }

  hydrateFromCompany(cp) {
    const b = emptyBusiness();
    this.setState({
      company: cp,
      business: {
        ...b,
        legalName: cp.legalName || '',
        // Re-hydrate the LP business sector so the picker shows the saved value on resume.
        sector: cp.sectorName || '',
        dbaName: cp.dbaName || '',
        ein: '', // never returned in full — leave blank, einLast4 shown separately
        headquartersAddressLine1: cp.headquartersAddressLine1 || '',
        headquartersCity: cp.headquartersCity || '',
        headquartersState: cp.headquartersState || '',
        headquartersZip: cp.headquartersZip || '',
        serviceRadiusMiles: cp.serviceRadiusMiles == null ? '' : String(cp.serviceRadiusMiles),
        serviceAreaMode: this.state.business.serviceAreaMode || 'TRAVEL',
        websiteUrl: cp.websiteUrl || '',
        logoUrl: cp.logoUrl || '',
        teamSize: this.state.business.teamSize || '',
        yearFounded: cp.yearFounded == null ? '' : String(cp.yearFounded),
        specialties: cp.specialties || [],
        operatingStates: cp.operatingStates || [],
      },
    });
  }

  async loadDocs() {
    const [lic, ins] = await Promise.all([lpApi(Q_MY_LICENSES), lpApi(Q_MY_INSURANCES)]);
    this.setState({
      licenses:
        (lic.data && lic.data.myLicenses && lic.data.myLicenses.licenses) || [],
      insurances:
        (ins.data && ins.data.myInsurances && ins.data.myInsurances.insurances) || [],
    });
  }

  // ---- Binary document upload (B1) -----------------------------------------
  // Posts the file to the besthand-web `/uploadCompanyDocument` route (multer,
  // src/core/upload/companyDocUpload.js), which writes it into DOCUMENT_UPLOAD_DIR
  // and returns an absolute `documentUrl`. We never send the binary to
  // besthand-api directly — the api only stores the URL (addLicense.documentUrl /
  // addInsurance.coiDocumentUrl), exactly like every other besthand-web upload.
  //
  // `slot` keys the per-target progress/error state. `logo` allows only images;
  // everything else also allows PDF. `onUrl(url)` receives the final URL.
  setUpload(slot, patch) {
    this.setState((st) => ({ uploads: { ...st.uploads, [slot]: { ...st.uploads[slot], ...patch } } }));
  }

  async uploadDoc(slot, file, onUrl) {
    if (!file) return;
    const allowed = slot === 'logo' ? ALLOWED_LOGO_TYPES : ALLOWED_DOC_TYPES;
    if (!allowed.includes(file.type)) {
      this.setUpload(slot, {
        uploading: false,
        error: slot === 'logo'
          ? 'Use a JPG, PNG, or WEBP image.'
          : 'Use a JPG, PNG, WEBP, or PDF file.',
        fileName: '',
      });
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      this.setUpload(slot, { uploading: false, error: 'File is too large (max 15 MB).', fileName: '' });
      return;
    }
    this.setUpload(slot, { uploading: true, error: '', fileName: file.name });
    try {
      const fd = new FormData();
      fd.append('document', file);
      const resp = await fetch('/uploadCompanyDocument', { method: 'POST', body: fd, credentials: 'same-origin' });
      const json = await resp.json();
      if (json && json.status === 200 && json.documentUrl) {
        this.setUpload(slot, { uploading: false, error: '', fileName: file.name, url: json.documentUrl });
        if (typeof onUrl === 'function') onUrl(json.documentUrl);
      } else {
        this.setUpload(slot, { uploading: false, error: (json && json.errorMessage) || 'Upload failed.', fileName: '' });
      }
    } catch (e) {
      this.setUpload(slot, { uploading: false, error: `Upload failed: ${e.message}`, fileName: '' });
    }
  }

  // eslint-disable-next-line class-methods-use-this
  firstError(res) {
    if (res && res.errors && res.errors.length) return res.errors[0].message;
    return 'Something went wrong.';
  }

  // Pull the operation envelope `{ status, errorMessage }` and surface a message.
  envelopeError(res, opName) {
    if (res.errors) return this.firstError(res);
    const env = res.data && res.data[opName];
    if (!env) return 'No response from server.';
    if (env.status && env.status >= 400) return env.errorMessage || `Error ${env.status}.`;
    return '';
  }

  setBiz(field, value) {
    this.setState((st) => ({ business: { ...st.business, [field]: value } }));
  }

  // Update a single in-app payout field. Changing the bank `country` also flips
  // the default representative country + currency and clears the now-irrelevant
  // bank fields (US routing vs CA transit/institution) so we never send both.
  setPay(field, value) {
    this.setState((st) => {
      const payout = { ...st.payout, [field]: value };
      if (field === 'country') {
        const ca = value === 'CA';
        payout.currency = ca ? 'cad' : 'usd';
        // Default the rep's home country to the bank country (user can change it).
        payout.repAddressCountry = value;
        // Clear the bank fields that don't apply to the chosen country.
        payout.routingNumber = ca ? '' : payout.routingNumber;
        payout.transitNumber = ca ? payout.transitNumber : '';
        payout.institutionNumber = ca ? payout.institutionNumber : '';
        // Province/state picker domain changes with country — reset the choice.
        payout.repAddressState = '';
      }
      return { payout };
    });
  }

  // True once the in-app payout form has the minimum the api needs to attach a
  // bank + representative (mirrors submitCompanyKyb's required-ish inputs).
  payoutComplete() {
    const p = this.state.payout;
    const bankOk = p.country === 'CA'
      ? Boolean(p.accountNumber && p.transitNumber && p.institutionNumber)
      : Boolean(p.accountNumber && p.routingNumber);
    const repOk = Boolean(
      p.repFirstName && p.repLastName
      && p.repDobDay && p.repDobMonth && p.repDobYear
      && p.repSsnLast4 && p.repAddressLine1 && p.repAddressCity
      && p.repAddressState && p.repAddressPostalCode
      && p.repEmail && p.repPhone,
    );
    return bankOk && repOk;
  }

  toggleSpecialty(code) {
    this.setState((st) => {
      const has = st.business.specialties.includes(code);
      const next = has
        ? st.business.specialties.filter((c) => c !== code)
        : [...st.business.specialties, code];
      return { business: { ...st.business, specialties: next } };
    });
  }

  toggleState(code) {
    this.setState((st) => {
      const has = st.business.operatingStates.includes(code);
      const next = has
        ? st.business.operatingStates.filter((c) => c !== code)
        : [...st.business.operatingStates, code];
      return { business: { ...st.business, operatingStates: next } };
    });
  }

  // ---- Step 1: create or update the company profile ----
  async saveBusiness() {
    const { business, company } = this.state;
    this.setState({ saving: true, error: '', notice: '' });
    try {
      let cp = company;
      if (!company || !company.id) {
        // First save -> createCompanyProfile (legalName/dba/ein/HQ only).
        const res = await lpApi(M_CREATE_COMPANY, {
          legalName: business.legalName,
          dbaName: business.dbaName || null,
          ein: business.ein || null,
          headquartersAddressLine1: business.headquartersAddressLine1 || null,
          headquartersCity: business.headquartersCity || null,
          headquartersState: business.headquartersState || null,
          headquartersZip: business.headquartersZip || null,
          sector: business.sector || null,
        });
        const err = this.envelopeError(res, 'createCompanyProfile');
        if (err) { this.setState({ saving: false, error: err }); return false; }
        cp = res.data.createCompanyProfile.companyProfile;
        if (typeof window !== 'undefined') {
          try { window.localStorage.setItem(LS_KEY, String(cp.id)); } catch (e) { /* noop */ }
        }
        this.setState({ company: cp });
      }

      // Always push the editable extras through updateCompanyProfile (these
      // fields aren't accepted by createCompanyProfile: specialties, states,
      // radius, website, tagline, yearFounded, address line 2).
      const upd = await lpApi(M_UPDATE_COMPANY, {
        dbaName: business.dbaName || null,
        headquartersAddressLine1: business.headquartersAddressLine1 || null,
        headquartersAddressLine2: null,
        headquartersCity: business.headquartersCity || null,
        headquartersState: business.headquartersState || null,
        headquartersZip: business.headquartersZip || null,
        serviceRadiusMiles: business.serviceRadiusMiles ? parseInt(business.serviceRadiusMiles, 10) : null,
        operatingStates: business.operatingStates,
        specialties: business.specialties,
        websiteUrl: business.websiteUrl || null,
        logoUrl: business.logoUrl || null,
        tagline: business.tagline || null,
        yearFounded: business.yearFounded ? parseInt(business.yearFounded, 10) : null,
        sector: business.sector || null,
      });
      const updErr = this.envelopeError(upd, 'updateCompanyProfile');
      if (updErr) { this.setState({ saving: false, error: updErr }); return false; }
      const fresh = upd.data.updateCompanyProfile.companyProfile;
      this.setState((st) => ({
        saving: false,
        notice: 'Business info saved.',
        company: { ...st.company, ...fresh },
      }));
      return true;
    } catch (e) {
      this.setState({ saving: false, error: `Save failed: ${e.message}` });
      return false;
    }
  }

  // ---- Step 2: documents ----
  async addLicense() {
    const { company, licenseDraft } = this.state;
    if (!company || !company.id) {
      this.setState({ error: 'Save your business info first (Step 1).' });
      return;
    }
    this.setState({ saving: true, error: '', notice: '' });
    try {
      const res = await lpApi(M_ADD_LICENSE, {
        companyId: String(company.id),
        type: licenseDraft.type,
        number: licenseDraft.number,
        state: licenseDraft.state,
        classification: licenseDraft.classification || null,
        issuingBody: licenseDraft.issuingBody || null,
        issueDate: licenseDraft.issueDate || null,
        expirationDate: licenseDraft.expirationDate || null,
        documentUrl: licenseDraft.documentUrl || null,
      });
      const err = this.envelopeError(res, 'addLicense');
      if (err) { this.setState({ saving: false, error: err }); return; }
      await this.loadDocs();
      this.setState((st) => ({
        saving: false,
        notice: 'License added.',
        licenseDraft: this.emptyLicense(),
        uploads: { ...st.uploads, license: {} },
      }));
    } catch (e) {
      this.setState({ saving: false, error: `Could not add license: ${e.message}` });
    }
  }

  async addInsurance() {
    const { company, insuranceDraft } = this.state;
    if (!company || !company.id) {
      this.setState({ error: 'Save your business info first (Step 1).' });
      return;
    }
    this.setState({ saving: true, error: '', notice: '' });
    try {
      const res = await lpApi(M_ADD_INSURANCE, {
        companyId: String(company.id),
        type: insuranceDraft.type,
        carrier: insuranceDraft.carrier,
        policyNumber: insuranceDraft.policyNumber,
        carrierPhone: insuranceDraft.carrierPhone || null,
        coverageAmount: insuranceDraft.coverageAmount ? parseFloat(insuranceDraft.coverageAmount) : null,
        expirationDate: insuranceDraft.expirationDate || null,
        coiDocumentUrl: insuranceDraft.coiDocumentUrl || null,
      });
      const err = this.envelopeError(res, 'addInsurance');
      if (err) { this.setState({ saving: false, error: err }); return; }
      await this.loadDocs();
      this.setState((st) => ({
        saving: false,
        notice: 'Insurance added.',
        insuranceDraft: this.emptyInsurance(),
        uploads: { ...st.uploads, insurance: {} },
      }));
    } catch (e) {
      this.setState({ saving: false, error: `Could not add insurance: ${e.message}` });
    }
  }

  // ---- Step 3 / 4: KYB submission via the in-app HYBRID payout flow ----
  // submitCompanyKyb now collects the bank + company representative IN-APP and
  // creates a Stripe Custom Connect account server-side (mirrors the worker
  // addPayout.js LIGHT flow). It transitions DRAFT->PENDING and returns either:
  //   stripeReady=true / currentlyDueCount=0  -> fully verified in-app, NO redirect.
  //   onboardingUrl (non-null)                -> Stripe still needs a few items;
  //                                             we surface it as a clearly-labelled
  //                                             SECONDARY link (no auto full-page
  //                                             redirect).
  async submitKyb() {
    const p = this.state.payout;
    this.setState({ saving: true, error: '', notice: '', payoutFallbackUrl: '' });
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const ca = p.country === 'CA';
      // Build the in-app HYBRID args. We only send the bank fields relevant to
      // the chosen country so Stripe never receives a conflicting pair.
      const vars = {
        // Hosted-onboarding FALLBACK redirect URLs (only used if Stripe still
        // has currently_due after we attach the bank + representative).
        refreshUrl: `${origin}/become-a-company?connect=refresh`,
        returnUrl: `${origin}/become-a-company?connect=return`,
        // Bank
        accountNumber: p.accountNumber,
        routingNumber: ca ? null : (p.routingNumber || null),
        transitNumber: ca ? (p.transitNumber || null) : null,
        institutionNumber: ca ? (p.institutionNumber || null) : null,
        currency: p.currency || (ca ? 'cad' : 'usd'),
        country: p.country,
        // Representative
        repFirstName: p.repFirstName,
        repLastName: p.repLastName,
        repDobDay: p.repDobDay ? parseInt(p.repDobDay, 10) : null,
        repDobMonth: p.repDobMonth ? parseInt(p.repDobMonth, 10) : null,
        repDobYear: p.repDobYear ? parseInt(p.repDobYear, 10) : null,
        repSsnLast4: p.repSsnLast4 || null,
        repAddressLine1: p.repAddressLine1 || null,
        repAddressCity: p.repAddressCity || null,
        repAddressState: p.repAddressState || null,
        repAddressPostalCode: p.repAddressPostalCode || null,
        repAddressCountry: p.repAddressCountry || p.country,
        repEmail: p.repEmail || null,
        repPhone: p.repPhone || null,
      };
      const res = await lpApi(M_SUBMIT_KYB, vars);
      const err = this.envelopeError(res, 'submitCompanyKyb');
      if (err) { this.setState({ saving: false, error: err }); return; }
      const env = res.data.submitCompanyKyb;
      const cp = env.companyProfile;
      // stripeReady is the authoritative "fully onboarded in-app" signal; fall
      // back to currentlyDueCount === 0 when stripeReady isn't returned.
      const ready = env.stripeReady === true
        || (env.stripeReady == null && env.currentlyDueCount === 0 && !env.onboardingUrl);
      if (env.onboardingUrl) {
        // Stripe still wants more (e.g. beneficial owners). Keep the link as a
        // SECONDARY action — never auto-redirect the whole page.
        this.setState((st) => ({
          saving: false,
          company: { ...st.company, ...cp },
          payoutFallbackUrl: env.onboardingUrl,
          notice: '',
        }));
        return;
      }
      this.setState((st) => ({
        saving: false,
        company: { ...st.company, ...cp },
        payoutFallbackUrl: '',
        notice: ready
          ? 'Payout details verified — your account is under review.'
          : 'Submitted for review.',
      }));
    } catch (e) {
      this.setState({ saving: false, error: `Submission failed: ${e.message}` });
    }
  }

  async syncKyb() {
    try {
      const res = await lpApi(M_SYNC_KYB);
      const env = res.data && res.data.syncCompanyKybStatus;
      if (env && env.companyProfile) {
        this.setState((st) => ({ company: { ...st.company, ...env.companyProfile } }));
      }
    } catch (e) { /* non-fatal: status tracker just stays as-is */ }
  }

  // ---- gating helpers (non-blocking nav; only the final submit is gated) ----
  businessComplete() {
    const b = this.state.business;
    return Boolean(
      (b.legalName || '').trim().length >= 2 &&
      (this.state.company && this.state.company.id),
    );
  }

  docsComplete() {
    const hasLicense = this.state.licenses.length >= 1;
    const hasGL = this.state.insurances.some((i) => i.type === 'GENERAL_LIABILITY');
    return hasLicense && hasGL;
  }

  canSubmit() {
    return this.businessComplete() && this.docsComplete();
  }

  goTo(i) {
    this.setState({ step: i, error: '', notice: '' });
  }

  async nextFromBusiness() {
    const ok = await this.saveBusiness();
    if (ok) this.goTo(1);
  }

  // ---- render ----
  render() {
    const { loading, authed } = this.state;

    // Not a confirmed company -> show the detached company LANDING (signup + sign-in)
    // as soon as we're not authed-as-company, INCLUDING during the initial load — so a
    // logged-out visitor clicking "Join as a company" gets the marketing landing, never
    // the wizard's "Loading your company…" tracker. The client `/login` and `/signup`
    // flows are never used or touched here.
    if (!authed) {
      return <CompanyAuth onAuthenticated={() => this.onAuthenticated()} />;
    }

    return (
      <div className={s.page}>
        <section className={s.hero}>
          <div className={s.wrap}>
            <span className={s.eyebrow}>BestHand Large Projects · for companies</span>
            <h1 className={s.h1}>
              Bring your company to <span className={s.hl}>bigger jobs.</span>
            </h1>
            <p className={s.sub}>
              Get verified once, then quote on large home-improvement and construction projects
              across your service area. Licenses, insurance, contracts, and payouts — all in one place.
            </p>
          </div>
        </section>

        <section className={s.body}>
          <div className={s.wrap}>
            {this.renderStatusTracker()}
            {loading ? (
              <div className={s.loading}>Loading your company…</div>
            ) : (
              <div className={s.layout}>
                {this.renderStepNav()}
                <div className={s.panel}>{this.renderStep()}</div>
              </div>
            )}
          </div>
        </section>
      </div>
    );
  }

  renderStatusTracker() {
    const status = (this.state.company && this.state.company.kybStatus) || 'DRAFT';
    // Map server status onto the visible flow. PENDING shows both "Submitted"
    // and "Under review" as active. REJECTED/SUSPENDED render a callout.
    const rank = { DRAFT: 0, PENDING: 2, APPROVED: 3 }[status];
    const activeIdx = rank == null ? 0 : rank;
    const rejected = status === 'REJECTED' || status === 'SUSPENDED';
    return (
      <div className={s.tracker}>
        <div className={s.trackerInner}>
          {STATUS_FLOW.map((stp, i) => {
            const done = i < activeIdx;
            const active = i === activeIdx && !rejected;
            const cls = [s.tNode, done ? s.tDone : '', active ? s.tActive : ''].join(' ');
            return (
              <React.Fragment key={stp.key}>
                <div className={cls}>
                  <span className={s.tDot}>{done ? '✓' : i + 1}</span>
                  <span className={s.tLabel}>{stp.label}</span>
                </div>
                {i < STATUS_FLOW.length - 1 && <span className={s.tBar} />}
              </React.Fragment>
            );
          })}
        </div>
        {rejected && (
          <div className={s.tReject}>
            {status === 'SUSPENDED' ? 'Your company is suspended.' : 'Your submission was rejected.'}
            {this.state.company && this.state.company.kybStatusReason
              ? ` ${this.state.company.kybStatusReason}`
              : ' Please review your details and re-submit.'}
          </div>
        )}
      </div>
    );
  }

  renderStepNav() {
    const { step } = this.state;
    const complete = [this.businessComplete(), this.docsComplete(),
      Boolean(this.state.company && this.state.company.stripeConnectAccountId),
      (this.state.company && this.state.company.kybStatus) === 'PENDING'
        || (this.state.company && this.state.company.kybStatus) === 'APPROVED'];
    return (
      <nav className={s.nav}>
        {STEPS.map((stp, i) => {
          const isActive = i === step;
          const isDone = complete[i];
          const cls = [s.navItem, isActive ? s.navActive : '', isDone ? s.navDone : ''].join(' ');
          return (
            <button type="button" key={stp.key} className={cls} onClick={() => this.goTo(i)}>
              <span className={s.navNo}>{isDone ? '✓' : i + 1}</span>
              <span className={s.navLabel}>{stp.label}</span>
            </button>
          );
        })}
      </nav>
    );
  }

  renderStep() {
    switch (this.state.step) {
      case 0: return this.renderBusiness();
      case 1: return this.renderDocuments();
      case 2: return this.renderConnect();
      case 3: return this.renderReview();
      default: return null;
    }
  }

  renderAlerts() {
    const { error, notice } = this.state;
    return (
      <div>
        {error ? <div className={s.alertErr}>{error}</div> : null}
        {notice ? <div className={s.alertOk}>{notice}</div> : null}
      </div>
    );
  }

  // Reusable upload drop-zone for a single document slot. Shows the picked file
  // name, an uploading state, a per-slot error, and a "uploaded ✓" confirmation.
  renderUploadField(slot, label, hint, onUrl, opts = {}) {
    const u = (this.state.uploads && this.state.uploads[slot]) || {};
    const accept = slot === 'logo' ? 'image/*' : 'image/*,application/pdf';
    return (
      <div className={s.upload}>
        <div className={s.uploadHead}>
          <span className={s.uploadLabel}>{label}</span>
          {opts.required ? <span className={s.uploadReq}>Required</span> : null}
        </div>
        {hint ? <p className={s.uploadHint}>{hint}</p> : null}
        <label className={`${s.uploadDrop} ${u.url ? s.uploadDropDone : ''}`}>
          <Icon d={u.url
            ? 'M20 6 9 17l-5-5'
            : 'M12 16V4m0 0L8 8m4-4 4 4M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2'} />
          <span className={s.uploadDropText}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {u.uploading
              ? 'Uploading…'
              : (u.url
                ? (u.fileName || 'File uploaded')
                : 'Click to choose a file (JPG, PNG, WEBP'.concat(slot === 'logo' ? ')' : ', or PDF)'))}
          </span>
          <input
            type="file"
            className={s.uploadInput}
            accept={accept}
            disabled={u.uploading || opts.disabled}
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              this.uploadDoc(slot, f, onUrl);
              // allow re-picking the same file later
              e.target.value = '';
            }}
          />
        </label>
        {u.url ? <span className={s.uploadOk}>Uploaded ✓</span> : null}
        {u.error ? <span className={s.uploadErr}>{u.error}</span> : null}
      </div>
    );
  }

  renderBusiness() {
    const b = this.state.business;
    const { saving, company } = this.state;
    const travels = b.serviceAreaMode === 'TRAVEL';
    return (
      <div>
        <h2 className={s.stepTitle}>Your company profile</h2>
        <p className={s.stepHint}>
          This is what customers see when you’re matched to a project. You can save and
          come back any time.
          {company && company.einLast4 ? ` EIN on file ends in ••${company.einLast4}.` : ''}
        </p>
        {this.renderAlerts()}

        {/* Logo + headline identity */}
        <div className={s.logoRow}>
          <div className={s.logoPreview}>
            {b.logoUrl
              ? <img src={b.logoUrl} alt="Company logo" className={s.logoImg} />
              : <Icon d="M3 21V5a2 2 0 0 1 2-2h6v18M11 21V9h8a2 2 0 0 1 2 2v10M7 7h.01M7 11h.01M7 15h.01M15 13h.01M15 17h.01" />}
          </div>
          <div className={s.logoUploadCell}>
            {this.renderUploadField(
              'logo',
              'Company logo or photo',
              'A clear logo or a photo of your crew/work. Shown on your profile and quotes.',
              (url) => this.setBiz('logoUrl', url),
            )}
          </div>
        </div>

        <div className={s.formGrid}>
          <label className={s.fieldFull}>
            <span>Legal business name *</span>
            <input className={s.input} value={b.legalName}
              onChange={(e) => this.setBiz('legalName', e.target.value)}
              placeholder="Acme Construction LLC" />
          </label>
          <label className={s.field}>
            <span>Business sector / domain *</span>
            <input className={s.input} value={b.sector}
              list="lpSectorsList"
              onChange={(e) => this.setBiz('sector', e.target.value)}
              placeholder="Search or type your sector (e.g. Roofing, Catering, Beauty & Hair)" />
            <datalist id="lpSectorsList">
              {(this.state.sectors || []).map((sec) => (
                <option key={sec.id} value={sec.name}>{sec.groupLabel || ''}</option>
              ))}
            </datalist>
          </label>
          <label className={s.field}>
            <span>DBA (optional)</span>
            <input className={s.input} value={b.dbaName}
              onChange={(e) => this.setBiz('dbaName', e.target.value)}
              placeholder="Acme Builders" />
          </label>
          <label className={s.field}>
            <span>EIN {company && company.einLast4 ? '(leave blank to keep)' : '(optional)'}</span>
            <input className={s.input} value={b.ein}
              onChange={(e) => this.setBiz('ein', e.target.value)}
              onBlur={() => this.middeskAutofill()}
              placeholder="12-3456789" />
            {this.state.middeskLookup.busy ? (
              <span style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Looking up business…</span>
            ) : (this.state.middeskLookup.notice ? (
              <span style={{ fontSize: 12, color: '#137a37', marginTop: 4 }}>{this.state.middeskLookup.notice}</span>
            ) : null)}
          </label>
          <label className={s.fieldFull}>
            <span>Short intro / tagline</span>
            <input className={s.input} value={b.tagline}
              onChange={(e) => this.setBiz('tagline', e.target.value)}
              placeholder="Quality remodels, on time and on budget." />
          </label>
          <label className={s.field}>
            <span>Year started</span>
            <input className={s.input} type="number" value={b.yearFounded}
              onChange={(e) => this.setBiz('yearFounded', e.target.value)} placeholder="2015" />
          </label>
          <label className={s.field}>
            <span>Team size</span>
            <select className={s.input} value={b.teamSize}
              onChange={(e) => this.setBiz('teamSize', e.target.value)}>
              <option value="">—</option>
              {TEAM_SIZES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
            </select>
          </label>
        </div>

        {/* Trades / services offered */}
        <div className={s.subBlock}>
          <h3 className={s.subTitle}>Services you offer</h3>
          <p className={s.stepHint}>Pick every trade your company takes on.</p>
          <div className={s.chips}>
            {SPECIALTIES.map(([code, label]) => {
              const on = b.specialties.includes(code);
              return (
                <button type="button" key={code}
                  className={`${s.chip} ${on ? s.chipOn : ''}`}
                  onClick={() => this.toggleSpecialty(code)}>{label}</button>
              );
            })}
          </div>
        </div>

        {/* Where you work — Thumbtack-style service-area model */}
        <div className={s.subBlock}>
          <h3 className={s.subTitle}>Where you work</h3>
          <div className={s.modeGrid}>
            {SERVICE_AREA_MODES.map(([code, title, desc]) => {
              const on = b.serviceAreaMode === code;
              return (
                <button type="button" key={code}
                  className={`${s.modeCard} ${on ? s.modeCardOn : ''}`}
                  onClick={() => this.setBiz('serviceAreaMode', code)}>
                  <span className={s.modeRadio} aria-hidden="true">{on ? '●' : ''}</span>
                  <span className={s.modeTitle}>{title}</span>
                  <span className={s.modeDesc}>{desc}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* HQ + radius/states. Radius is most relevant when you travel. */}
        <div className={s.subBlock}>
          <h3 className={s.subTitle}>Home base &amp; coverage</h3>
          <div className={s.formGrid}>
            <label className={s.fieldFull}>
              <span>Headquarters address</span>
              <input className={s.input} value={b.headquartersAddressLine1}
                onChange={(e) => this.setBiz('headquartersAddressLine1', e.target.value)}
                placeholder="123 Main St" />
            </label>
            <label className={s.field}>
              <span>City</span>
              <input className={s.input} value={b.headquartersCity}
                onChange={(e) => this.setBiz('headquartersCity', e.target.value)} />
            </label>
            <label className={s.field}>
              <span>State</span>
              <select className={s.input} value={b.headquartersState}
                onChange={(e) => this.setBiz('headquartersState', e.target.value)}>
                <option value="">—</option>
                {US_STATES.map((st) => <option key={st} value={st}>{st}</option>)}
              </select>
            </label>
            <label className={s.field}>
              <span>{travels ? 'Home base ZIP' : 'ZIP'}</span>
              <input className={s.input} value={b.headquartersZip}
                onChange={(e) => this.setBiz('headquartersZip', e.target.value)} />
            </label>
            <label className={s.field}>
              <span>Travel radius (miles){travels ? '' : ' — optional'}</span>
              <input className={s.input} type="number" value={b.serviceRadiusMiles}
                onChange={(e) => this.setBiz('serviceRadiusMiles', e.target.value)}
                placeholder="50" />
            </label>
          </div>
          <div className={s.subBlockTight}>
            <span className={s.miniLabel}>Operating states</span>
            <div className={s.chips}>
              {US_STATES.map((st) => {
                const on = b.operatingStates.includes(st);
                return (
                  <button type="button" key={st}
                    className={`${s.chipSm} ${on ? s.chipOn : ''}`}
                    onClick={() => this.toggleState(st)}>{st}</button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Online presence */}
        <div className={s.subBlock}>
          <h3 className={s.subTitle}>Online presence (optional)</h3>
          <div className={s.formGrid}>
            <label className={s.fieldFull}>
              <span>Website</span>
              <input className={s.input} value={b.websiteUrl}
                onChange={(e) => this.setBiz('websiteUrl', e.target.value)}
                placeholder="https://" />
            </label>
          </div>
        </div>

        <div className={s.actions}>
          <button type="button" className={s.btnGhost}
            disabled={saving || (b.legalName || '').trim().length < 2}
            onClick={() => this.saveBusiness()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className={s.btn}
            disabled={saving || (b.legalName || '').trim().length < 2}
            onClick={() => this.nextFromBusiness()}>
            Save &amp; continue
          </button>
        </div>
      </div>
    );
  }

  renderDocuments() {
    const { licenses, insurances, licenseDraft, insuranceDraft, saving, company } = this.state;
    return (
      <div>
        <h2 className={s.stepTitle}>Verification &amp; credentials</h2>
        <p className={s.stepHint}>
          Add at least one contractor license and a General Liability insurance policy, and
          upload the supporting document for each. An admin verifies every file after you submit —
          this is what lets clients trust your company over an unverified listing.
        </p>
        {this.renderAlerts()}
        {!(company && company.id) && (
          <div className={s.alertErr}>Save your business info first (Step 1) to attach documents.</div>
        )}

        {/* Licenses */}
        <div className={s.subBlock}>
          <h3 className={s.subTitle}>Licenses</h3>
          {licenses.length === 0 && <p className={s.empty}>No licenses added yet.</p>}
          {licenses.map((l) => (
            <div className={s.docRow} key={l.id}>
              <div>
                <strong>{l.type}</strong> · {l.number} · {l.state}
              </div>
              <span className={`${s.tag} ${l.verificationStatus === 'VERIFIED' ? s.tagOk : ''}`}>
                {l.verificationStatus || 'PENDING'}
              </span>
            </div>
          ))}
          <div className={s.formGrid}>
            <label className={s.field}>
              <span>Type</span>
              <select className={s.input} value={licenseDraft.type}
                onChange={(e) => this.setState({ licenseDraft: { ...licenseDraft, type: e.target.value } })}>
                {LICENSE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label className={s.field}>
              <span>License number</span>
              <input className={s.input} value={licenseDraft.number}
                onChange={(e) => this.setState({ licenseDraft: { ...licenseDraft, number: e.target.value } })} />
            </label>
            <label className={s.field}>
              <span>State</span>
              <select className={s.input} value={licenseDraft.state}
                onChange={(e) => this.setState({ licenseDraft: { ...licenseDraft, state: e.target.value } })}>
                <option value="">—</option>
                {US_STATES.map((st) => <option key={st} value={st}>{st}</option>)}
              </select>
            </label>
            <label className={s.field}>
              <span>Expiration date</span>
              <input className={s.input} type="date" value={licenseDraft.expirationDate}
                onChange={(e) => this.setState({ licenseDraft: { ...licenseDraft, expirationDate: e.target.value } })} />
            </label>
          </div>
          {this.renderUploadField(
            'license',
            'License document',
            'A clear photo or scan of the license (image or PDF).',
            (url) => this.setState((st) => ({ licenseDraft: { ...st.licenseDraft, documentUrl: url } })),
            { disabled: !company || !company.id },
          )}
          <button type="button" className={s.btnGhost}
            disabled={saving || !company || !company.id || !licenseDraft.number || !licenseDraft.state}
            onClick={() => this.addLicense()}>Add license</button>
        </div>

        {/* Insurance */}
        <div className={s.subBlock}>
          <h3 className={s.subTitle}>Insurance</h3>
          {insurances.length === 0 && <p className={s.empty}>No policies added yet.</p>}
          {insurances.map((i) => (
            <div className={s.docRow} key={i.id}>
              <div>
                <strong>{i.type}</strong> · {i.carrier} · {i.policyNumber}
              </div>
              <span className={`${s.tag} ${i.verificationStatus === 'VERIFIED' ? s.tagOk : ''}`}>
                {i.verificationStatus || 'PENDING'}
              </span>
            </div>
          ))}
          <div className={s.formGrid}>
            <label className={s.field}>
              <span>Type</span>
              <select className={s.input} value={insuranceDraft.type}
                onChange={(e) => this.setState({ insuranceDraft: { ...insuranceDraft, type: e.target.value } })}>
                {INSURANCE_TYPES.map(([t, label]) => <option key={t} value={t}>{label}</option>)}
              </select>
            </label>
            <label className={s.field}>
              <span>Carrier</span>
              <input className={s.input} value={insuranceDraft.carrier}
                onChange={(e) => this.setState({ insuranceDraft: { ...insuranceDraft, carrier: e.target.value } })} />
            </label>
            <label className={s.field}>
              <span>Policy number</span>
              <input className={s.input} value={insuranceDraft.policyNumber}
                onChange={(e) => this.setState({ insuranceDraft: { ...insuranceDraft, policyNumber: e.target.value } })} />
            </label>
            <label className={s.field}>
              <span>Coverage amount ($)</span>
              <input className={s.input} type="number" value={insuranceDraft.coverageAmount}
                onChange={(e) => this.setState({ insuranceDraft: { ...insuranceDraft, coverageAmount: e.target.value } })} />
            </label>
            <label className={s.field}>
              <span>Carrier phone</span>
              <input className={s.input} value={insuranceDraft.carrierPhone}
                onChange={(e) => this.setState({ insuranceDraft: { ...insuranceDraft, carrierPhone: e.target.value } })} />
            </label>
            <label className={s.field}>
              <span>Expiration date</span>
              <input className={s.input} type="date" value={insuranceDraft.expirationDate}
                onChange={(e) => this.setState({ insuranceDraft: { ...insuranceDraft, expirationDate: e.target.value } })} />
            </label>
          </div>
          {this.renderUploadField(
            'insurance',
            'Certificate of Insurance (COI)',
            'Upload the COI from your carrier (image or PDF).',
            (url) => this.setState((st) => ({ insuranceDraft: { ...st.insuranceDraft, coiDocumentUrl: url } })),
            { disabled: !company || !company.id },
          )}
          <button type="button" className={s.btnGhost}
            disabled={saving || !company || !company.id || !insuranceDraft.carrier || !insuranceDraft.policyNumber}
            onClick={() => this.addInsurance()}>Add insurance</button>
        </div>

        {/* Owner identity — we capture a government ID now (admin review) AND
            Stripe re-verifies the owner during Connect onboarding (next step). */}
        <div className={s.subBlock}>
          <h3 className={s.subTitle}>Owner identity</h3>
          <p className={s.stepHint}>
            Upload a government-issued photo ID for the company owner. Stripe also verifies the
            owner securely during the Payouts step — this copy is for BestHand admin review.
          </p>
          {this.renderUploadField(
            'ownerId',
            'Owner government ID',
            "Driver's license or passport (image or PDF).",
            (url) => this.setState({ ownerIdUrl: url }),
            { disabled: !company || !company.id },
          )}
        </div>

        <div className={s.actions}>
          <button type="button" className={s.btnGhost} onClick={() => this.goTo(0)}>Back</button>
          <button type="button" className={s.btn} onClick={() => this.goTo(2)}>Continue</button>
        </div>
      </div>
    );
  }

  renderConnect() {
    const { company, saving, payout: p, payoutFallbackUrl } = this.state;
    const connected = Boolean(company && company.stripeConnectAccountId);
    const ca = p.country === 'CA';
    const ssnLabel = ca ? 'SIN — last 4 digits' : 'SSN — last 4 digits';
    const stateLabel = ca ? 'Province' : 'State';
    const stateDomain = ca ? CA_PROVINCES : US_STATES;
    const postalLabel = ca ? 'Postal code' : 'ZIP code';
    // The form was already accepted once and Stripe needs nothing more in-app.
    const verifiedInApp = connected
      && (company.kybStatus === 'PENDING' || company.kybStatus === 'APPROVED')
      && !payoutFallbackUrl;
    return (
      <div>
        <h2 className={s.stepTitle}>Set up payouts</h2>
        <p className={s.stepHint}>
          BestHand pays your company through Stripe. Enter your business bank account and
          the details of the person who controls the company — we verify everything securely
          in the background. Your bank and ID details are sent straight to Stripe and are
          never stored by BestHand.
        </p>
        {this.renderAlerts()}

        {verifiedInApp ? (
          <div className={s.successCard}>
            <div className={s.connectIc}>
              <Icon d="M20 6 9 17l-5-5" />
            </div>
            <div className={s.connectBody}>
              <h3>Payout details verified — your account is under review</h3>
              <p>
                We&apos;ve received your bank account and representative details. No further
                action is needed here; we&apos;ll let you know once verification completes.
              </p>
            </div>
          </div>
        ) : (
          <div>
            {/* Bank account — country picker drives which fields show. */}
            <div className={s.subBlock}>
              <h3 className={s.subTitle}>Business bank account</h3>
              <div className={s.formGrid}>
                <label className={s.field}>
                  <span>Country</span>
                  <select className={s.input} value={p.country}
                    onChange={(e) => this.setPay('country', e.target.value)}>
                    {PAYOUT_COUNTRIES.map(([code, label]) => (
                      <option key={code} value={code}>{label}</option>
                    ))}
                  </select>
                </label>
                <label className={s.field}>
                  <span>Account number</span>
                  <input className={s.input} value={p.accountNumber}
                    onChange={(e) => this.setPay('accountNumber', e.target.value)}
                    placeholder="000123456789" />
                </label>
                {ca ? (
                  <React.Fragment>
                    <label className={s.field}>
                      <span>Transit number</span>
                      <input className={s.input} value={p.transitNumber}
                        onChange={(e) => this.setPay('transitNumber', e.target.value)}
                        placeholder="12345" />
                    </label>
                    <label className={s.field}>
                      <span>Institution number</span>
                      <input className={s.input} value={p.institutionNumber}
                        onChange={(e) => this.setPay('institutionNumber', e.target.value)}
                        placeholder="001" />
                    </label>
                  </React.Fragment>
                ) : (
                  <label className={s.field}>
                    <span>Routing number</span>
                    <input className={s.input} value={p.routingNumber}
                      onChange={(e) => this.setPay('routingNumber', e.target.value)}
                      placeholder="110000000" />
                  </label>
                )}
              </div>
            </div>

            {/* Company representative (the person who controls the business). */}
            <div className={s.subBlock}>
              <h3 className={s.subTitle}>Company representative</h3>
              <p className={s.stepHint}>
                The owner or an executive who can act on behalf of the company. Stripe is
                required by law to verify this person&apos;s identity.
              </p>
              <div className={s.formGrid}>
                <label className={s.field}>
                  <span>First name</span>
                  <input className={s.input} value={p.repFirstName}
                    onChange={(e) => this.setPay('repFirstName', e.target.value)} />
                </label>
                <label className={s.field}>
                  <span>Last name</span>
                  <input className={s.input} value={p.repLastName}
                    onChange={(e) => this.setPay('repLastName', e.target.value)} />
                </label>
                <label className={s.field}>
                  <span>Email</span>
                  <input className={s.input} type="email" value={p.repEmail}
                    onChange={(e) => this.setPay('repEmail', e.target.value)}
                    placeholder="owner@company.com" />
                </label>
                <label className={s.field}>
                  <span>Phone</span>
                  <input className={s.input} value={p.repPhone}
                    onChange={(e) => this.setPay('repPhone', e.target.value)}
                    placeholder="+1 555 123 4567" />
                </label>
              </div>

              <div className={s.subBlockTight}>
                <span className={s.miniLabel}>Date of birth</span>
                <div className={s.formGrid}>
                  <label className={s.field}>
                    <span>Day</span>
                    <input className={s.input} type="number" min="1" max="31" value={p.repDobDay}
                      onChange={(e) => this.setPay('repDobDay', e.target.value)} placeholder="DD" />
                  </label>
                  <label className={s.field}>
                    <span>Month</span>
                    <input className={s.input} type="number" min="1" max="12" value={p.repDobMonth}
                      onChange={(e) => this.setPay('repDobMonth', e.target.value)} placeholder="MM" />
                  </label>
                  <label className={s.field}>
                    <span>Year</span>
                    <input className={s.input} type="number" value={p.repDobYear}
                      onChange={(e) => this.setPay('repDobYear', e.target.value)} placeholder="YYYY" />
                  </label>
                  <label className={s.field}>
                    <span>{ssnLabel}</span>
                    <input className={s.input} value={p.repSsnLast4} maxLength={4}
                      onChange={(e) => this.setPay('repSsnLast4', e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="1234" />
                  </label>
                </div>
              </div>

              <div className={s.subBlockTight}>
                <span className={s.miniLabel}>Home address</span>
                <div className={s.formGrid}>
                  <label className={s.fieldFull}>
                    <span>Street address</span>
                    <input className={s.input} value={p.repAddressLine1}
                      onChange={(e) => this.setPay('repAddressLine1', e.target.value)}
                      placeholder="123 Main St" />
                  </label>
                  <label className={s.field}>
                    <span>City</span>
                    <input className={s.input} value={p.repAddressCity}
                      onChange={(e) => this.setPay('repAddressCity', e.target.value)} />
                  </label>
                  <label className={s.field}>
                    <span>{stateLabel}</span>
                    <select className={s.input} value={p.repAddressState}
                      onChange={(e) => this.setPay('repAddressState', e.target.value)}>
                      <option value="">—</option>
                      {stateDomain.map((st) => <option key={st} value={st}>{st}</option>)}
                    </select>
                  </label>
                  <label className={s.field}>
                    <span>{postalLabel}</span>
                    <input className={s.input} value={p.repAddressPostalCode}
                      onChange={(e) => this.setPay('repAddressPostalCode', e.target.value)} />
                  </label>
                </div>
              </div>
            </div>

            {!this.docsComplete() && (
              <div className={s.alertErr}>
                Add at least one license and a General Liability policy (Step 2) before setting up payouts.
              </div>
            )}

            {/* Hosted-onboarding FALLBACK: shown only when Stripe still needs a
                few items (e.g. beneficial owners). A clearly-labelled SECONDARY
                action — NOT an automatic full-page redirect. */}
            {payoutFallbackUrl ? (
              <div className={s.fallbackCard}>
                <p className={s.fallbackText}>
                  Almost there. Stripe needs a few more verification details (for example,
                  any additional owners of the company).
                </p>
                <a className={s.fallbackLink} href={payoutFallbackUrl}
                  target="_blank" rel="noopener noreferrer">
                  Finish a few remaining verification steps on Stripe →
                </a>
              </div>
            ) : null}

            <div className={s.actions}>
              <button type="button" className={s.btnGhost} onClick={() => this.goTo(1)}>Back</button>
              <button type="button" className={s.btn}
                disabled={saving || !this.docsComplete() || !this.payoutComplete()}
                onClick={() => this.submitKyb()}>
                {/* eslint-disable-next-line no-nested-ternary */}
                {saving
                  ? 'Verifying…'
                  : (payoutFallbackUrl ? 'Re-submit payout details' : 'Verify & submit payouts')}
              </button>
              <button type="button" className={s.btnText} onClick={() => this.goTo(3)}>Skip for now</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  renderReview() {
    const { company, business, licenses, insurances, saving } = this.state;
    const status = (company && company.kybStatus) || 'DRAFT';
    const submitted = status === 'PENDING' || status === 'APPROVED';
    return (
      <div>
        <h2 className={s.stepTitle}>Review &amp; submit</h2>
        <p className={s.stepHint}>Double-check everything, then submit your company for verification.</p>
        {this.renderAlerts()}

        <div className={s.review}>
          <div className={s.reviewSec}>
            <h3>Company profile</h3>
            <dl className={s.dl}>
              <dt>Legal name</dt><dd>{business.legalName || '—'}</dd>
              <dt>DBA</dt><dd>{business.dbaName || '—'}</dd>
              <dt>EIN</dt><dd>{company && company.einLast4 ? `••${company.einLast4}` : '—'}</dd>
              <dt>Logo</dt><dd>{business.logoUrl ? 'Uploaded ✓' : '—'}</dd>
              <dt>Tagline</dt><dd>{business.tagline || '—'}</dd>
              <dt>Year started</dt><dd>{business.yearFounded || '—'}</dd>
              <dt>Team size</dt>
              <dd>
                {(TEAM_SIZES.find(([c]) => c === business.teamSize) || [])[1] || '—'}
              </dd>
              <dt>Where you work</dt>
              <dd>
                {(SERVICE_AREA_MODES.find(([c]) => c === business.serviceAreaMode) || [])[1] || '—'}
              </dd>
              <dt>HQ</dt>
              <dd>
                {[business.headquartersAddressLine1, business.headquartersCity,
                  business.headquartersState, business.headquartersZip].filter(Boolean).join(', ') || '—'}
              </dd>
              <dt>Travel radius</dt>
              <dd>{business.serviceRadiusMiles ? `${business.serviceRadiusMiles} mi` : '—'}</dd>
              <dt>Services</dt><dd>{business.specialties.join(', ') || '—'}</dd>
              <dt>Operating states</dt><dd>{business.operatingStates.join(', ') || '—'}</dd>
            </dl>
          </div>

          <div className={s.reviewSec}>
            <h3>Licenses ({licenses.length})</h3>
            {licenses.length === 0 ? <p className={s.empty}>None</p> : licenses.map((l) => (
              <div className={s.docRow} key={l.id}>
                <span>{l.type} · {l.number} · {l.state}</span>
                <span className={s.tag}>{l.verificationStatus || 'PENDING'}</span>
              </div>
            ))}
          </div>

          <div className={s.reviewSec}>
            <h3>Insurance ({insurances.length})</h3>
            {insurances.length === 0 ? <p className={s.empty}>None</p> : insurances.map((i) => (
              <div className={s.docRow} key={i.id}>
                <span>{i.type} · {i.carrier}</span>
                <span className={s.tag}>{i.verificationStatus || 'PENDING'}</span>
              </div>
            ))}
          </div>
        </div>

        {!this.canSubmit() && (
          <div className={s.alertErr}>
            Before submitting: save your business info, add ≥1 license, and add a General
            Liability insurance policy.
          </div>
        )}
        {this.canSubmit() && !submitted && !this.payoutComplete() && (
          <div className={s.alertErr}>
            One last step: add your bank account and company representative in the Payouts step
            so we can verify your account and pay you.
          </div>
        )}

        <div className={s.actions}>
          <button type="button" className={s.btnGhost} onClick={() => this.goTo(2)}>Back</button>
          {/* eslint-disable-next-line no-nested-ternary */}
          {submitted ? (
            <button type="button" className={s.btn} onClick={() => this.syncKyb()}>
              Refresh status
            </button>
          ) : this.payoutComplete() ? (
            // Payout details already filled in the Payouts step — submit directly.
            <button type="button" className={s.btn}
              disabled={saving || !this.canSubmit()}
              onClick={() => this.submitKyb()}>
              {saving ? 'Submitting…' : 'Submit for verification'}
            </button>
          ) : (
            // Payout form not complete — send the user to the Payouts step where
            // the in-app bank + representative form lives (and submits).
            <button type="button" className={s.btn}
              disabled={!this.canSubmit()}
              onClick={() => this.goTo(2)}>
              Continue to payouts
            </button>
          )}
        </div>
      </div>
    );
  }
}

export default withStyles(s)(BecomeACompany);
